import { useEffect, useRef } from "react";

const VERT_SRC = `
attribute vec2 a_position;
void main() {
  gl_Position = vec4(a_position, 0.0, 1.0);
}
`;

const FRAG_SRC = `
precision highp float;
uniform float u_time;
uniform vec2 u_resolution;

// Pseudo-random hash
float hash(vec2 p) {
    p = fract(p * vec2(123.34, 456.21));
    p += dot(p, p + 45.32);
    return fract(p.x * p.y);
}

// 2D Value Noise
float noise(vec2 p) {
    vec2 i = floor(p);
    vec2 f = fract(p);
    vec2 u = f * f * (3.0 - 2.0 * f);
    return mix(mix(hash(i + vec2(0.0,0.0)), hash(i + vec2(1.0,0.0)), u.x),
               mix(hash(i + vec2(0.0,1.0)), hash(i + vec2(1.0,1.0)), u.x), u.y);
}

// Rotation matrix
mat2 rot(float a) {
    float s = sin(a), c = cos(a);
    return mat2(c, -s, s, c);
}

// Fractal Brownian Motion
float fbm(vec2 p) {
    float v = 0.0;
    float a = 0.5;
    vec2 shift = vec2(100.0);
    mat2 r = rot(0.5);
    for (int i = 0; i < 6; i++) {
        v += a * noise(p);
        p = r * p * 2.0 + shift;
        a *= 0.5;
    }
    return v;
}

void main() {
    // Normalize and aspect-correct UVs
    vec2 uv = gl_FragCoord.xy / u_resolution.xy;
    vec2 p = uv * 2.0 - 1.0;
    p.x *= u_resolution.x / u_resolution.y;

    // Time scaling for slow hypnotic movement
    float t = u_time * 0.15;

    // Domain Warping - Level 1
    vec2 q = vec2(
        fbm(p * 2.0 + vec2(0.0, t * 1.5)),
        fbm(p * 2.0 + vec2(5.2, 1.3) - t * 0.8)
    );

    // Domain Warping - Level 2
    vec2 r = vec2(
        fbm(p * 3.0 + 4.0 * q + vec2(1.7, 9.2) + t * 2.0),
        fbm(p * 3.0 + 4.0 * q + vec2(8.3, 2.8) - t * 1.5)
    );

    // Final noise field
    float f = fbm(p * 2.5 + 4.0 * r);

    // --- Color Palette ---
    vec3 cBg  = vec3(0.02, 0.00, 0.05); // Deep black / very dark purple
    vec3 cP1  = vec3(0.42, 0.00, 1.00); // Saturated Purple (#6a00ff)
    vec3 cP2  = vec3(0.63, 0.12, 0.94); // Violet (#a020f0)
    vec3 cP3  = vec3(1.00, 0.00, 1.00); // Magenta (#ff00ff)
    vec3 cGrn = vec3(0.22, 1.00, 0.08); // Neon Green (#39ff14)

    // Blend base purples organically using domain warped layers
    vec3 col = mix(cBg, cP1, smoothstep(0.0, 0.5, f));
    col = mix(col, cP2, smoothstep(0.2, 0.8, q.x));
    col = mix(col, cP3, smoothstep(0.4, 1.0, r.y));

    // Enhance contrast to make the liquid feel volumetric
    col *= f * 1.8 + 0.2;

    // --- Sharp Veins & Glow ---
    // Create valleys where noise crosses 0.5
    float veinMap = abs(f - 0.5) * 2.0;
    float veinMap2 = abs(r.x - 0.4) * 2.0;

    // Hard core of the vein
    float vein1 = 1.0 - smoothstep(0.0, 0.03, veinMap);
    float vein2 = 1.0 - smoothstep(0.0, 0.04, veinMap2);

    // Emissive glow spreading out from the veins
    float glow1 = 1.0 - smoothstep(0.0, 0.20, veinMap);
    float glow2 = 1.0 - smoothstep(0.0, 0.25, veinMap2);

    // Break up veins so they look organic rather than continuous infinite lines
    float veinMask = smoothstep(0.3, 0.7, noise(p * 5.0 + t));
    
    vec3 neon1 = cGrn * (vein1 * 2.5 + glow1 * 0.9) * veinMask;
    vec3 neon2 = cGrn * (vein2 * 2.0 + glow2 * 0.7) * (1.0 - veinMask);

    col += neon1 + neon2;

    // --- Post Processing ---
    // Smooth vignette
    float vig = length(uv - 0.5);
    col *= 1.0 - (vig * 1.2);

    // Very subtle noise/grain for texture richness
    float grain = hash(uv * 200.0 + t) * 0.03;
    col += grain;

    gl_FragColor = vec4(clamp(col, 0.0, 1.0), 1.0);
}
`;

function createShader(gl: WebGLRenderingContext, type: number, src: string) {
  const s = gl.createShader(type);
  if (!s) return null;
  gl.shaderSource(s, src);
  gl.compileShader(s);
  if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
    console.error(gl.getShaderInfoLog(s));
    gl.deleteShader(s);
    return null;
  }
  return s;
}

function createProgram(gl: WebGLRenderingContext, vert: string, frag: string) {
  const vs = createShader(gl, gl.VERTEX_SHADER, vert);
  const fs = createShader(gl, gl.FRAGMENT_SHADER, frag);
  if (!vs || !fs) return null;
  const prog = gl.createProgram();
  if (!prog) return null;
  gl.attachShader(prog, vs);
  gl.attachShader(prog, fs);
  gl.linkProgram(prog);
  if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
    console.error(gl.getProgramInfoLog(prog));
    return null;
  }
  return prog;
}

export default function WebGLCyberfluidBackground() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rafRef    = useRef<number>(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const gl = canvas.getContext("webgl", { alpha: false, antialias: true });
    if (!gl) return;
    
    const program = createProgram(gl, VERT_SRC, FRAG_SRC);
    if (!program) return;
    
    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([
      -1,-1,  1,-1,  -1,1,
      -1,1,   1,-1,   1,1
    ]), gl.STATIC_DRAW);
    
    const posLoc  = gl.getAttribLocation(program, "a_position");
    const timeLoc = gl.getUniformLocation(program, "u_time");
    const resLoc  = gl.getUniformLocation(program, "u_resolution");
    
    if (gl.isContextLost()) return;
    
    gl.useProgram(program);
    gl.enableVertexAttribArray(posLoc);
    gl.vertexAttribPointer(posLoc, 2, gl.FLOAT, false, 0, 0);

    let start: number | null = null;
    const resize = () => {
      canvas.width  = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
      gl.viewport(0, 0, canvas.width, canvas.height);
    };
    
    const observer = new ResizeObserver(resize);
    observer.observe(canvas);
    resize();

    const render = (ts: number) => {
      if (start === null) start = ts;
      const t = (ts - start) / 1000;
      if (gl.isContextLost()) return;
      
      gl.uniform1f(timeLoc, t);
      gl.uniform2f(resLoc, canvas.width, canvas.height);
      gl.drawArrays(gl.TRIANGLES, 0, 6);
      
      rafRef.current = requestAnimationFrame(render);
    };
    rafRef.current = requestAnimationFrame(render);

    return () => {
      cancelAnimationFrame(rafRef.current);
      observer.disconnect();
      gl.getExtension('WEBGL_lose_context')?.loseContext();
    };
  }, []);

  return <canvas ref={canvasRef} className="absolute inset-0 w-full h-full pointer-events-none" />;
}
